import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

// Global ref for the deferred prompt
let deferredPrompt = null;

// Listen for the beforeinstallprompt event globally
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
}

const PWAInstallPrompt = ({ trigger }) => {
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  const shouldShow = useCallback(() => {
    // Don't show if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return false;
    if (window.navigator.standalone) return false;

    // Check if dismissed recently (24h cooldown)
    const dismissed = localStorage.getItem('pwa_dismissed_at');
    if (dismissed && Date.now() - parseInt(dismissed) < 24 * 60 * 60 * 1000) return false;

    return true;
  }, []);

  useEffect(() => {
    if (!trigger || !shouldShow()) return;

    // Check iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    // Show for iOS (manual instructions) or when prompt is available
    if (isiOS || deferredPrompt) {
      const timer = setTimeout(() => setShow(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [trigger, shouldShow]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        localStorage.setItem('pwa_installed', 'true');
      }
      deferredPrompt = null;
    }
    setShow(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_dismissed_at', Date.now().toString());
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[200] flex justify-center" data-testid="pwa-install-prompt">
      <div className="bg-black border border-gray-800 rounded-2xl p-5 max-w-sm w-full shadow-2xl">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white dark:bg-neutral-900 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-black dark:text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-sm mb-1">Get the Connectify App</h3>
            <p className="text-gray-400 dark:text-neutral-500 text-xs leading-relaxed">
              {isIOS
                ? 'Tap the share button, then "Add to Home Screen" for the full app experience.'
                : 'Install for quick access, offline support, and a native app experience.'}
            </p>
          </div>
          <button onClick={handleDismiss} className="text-gray-500 dark:text-neutral-500 hover:text-gray-300 flex-shrink-0 mt-0.5">
            <X size={18} />
          </button>
        </div>

        {!isIOS && (
          <button
            onClick={handleInstall}
            className="w-full mt-4 py-3 bg-white dark:bg-neutral-900 text-black dark:text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
            data-testid="pwa-install-btn"
          >
            <Download size={16} />
            Install App
          </button>
        )}

        {isIOS && (
          <div className="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-neutral-500 text-xs">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
            <span>Tap Share → Add to Home Screen</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Hook: tracks dashboard visits and returns whether to show the prompt
export const usePWADashboardTrigger = () => {
  const [shouldTrigger, setShouldTrigger] = useState(false);

  useEffect(() => {
    // Already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;
    if (localStorage.getItem('pwa_installed')) return;

    const count = parseInt(localStorage.getItem('pwa_dashboard_visits') || '0') + 1;
    localStorage.setItem('pwa_dashboard_visits', count.toString());

    // Trigger every 2nd visit (2nd, 4th, 6th...)
    if (count % 2 === 0) {
      setShouldTrigger(true);
    }
  }, []);

  return shouldTrigger;
};

export default PWAInstallPrompt;
